import { FC, HTMLAttributes } from "react";
import clsx from "classnames";
import { useStyles } from "./NotificationItem.style";
import Avatar from "../../core/Avatar/Avatar";
import { Typography } from "../../core/Typography/Typography";
import { TypographyVariant } from "../../../constants/typography";
import { NotificationMd } from "../../../models/notification";
import { StaticImage } from "../StaticImage";
import GzCoinIcon from "../../../assets/rankGem/webp/gz_coin_1x.webp";
import GzCoinIcon2x from "../../../assets/rankGem/webp/icon2x/gz_coin_2x.webp";
import GzCoinIcon3x from "../../../assets/rankGem/webp/icon3x/gz_coin_3x.webp";
import { Icon } from "../../core/Icon/Icon";
import { closeNotification } from "../../../store/slices/inAppNotification";
import { useAppDispatch } from "../../hooks";
import {
  AchievementUserNotificationData,
  FollowUserNotificationData,
  UserNotification,
  UserNotificationType,
} from "@onmo/graphql/graphql";
import { useNavigate } from "react-router-dom";

interface INotificationItemProps {
  notification: UserNotification;
  isInApp?: boolean;
  onClose?: () => void;
}

const renderAvatar = (notification: UserNotification) => {
  switch (notification?.type) {
    case UserNotificationType.FollowedByUser:
      return (
        <Avatar
          url={
            (notification.data as FollowUserNotificationData)?.followerUser
              ?.avatar
          }
          size="m"
          alt="avatar-notification"
        />
      );
    case UserNotificationType.GlobalLeaderboardEnded:
      return (
        <StaticImage
          src={GzCoinIcon}
          src2x={GzCoinIcon2x}
          src3x={GzCoinIcon3x}
          width={50}
          height={50}
        />
      );
    case UserNotificationType.AchievementReward: {
      const achievementRewardData =
        notification.data as AchievementUserNotificationData;
      const icons = NotificationMd.getAchievementIconsFromIdAndTier(
        achievementRewardData.type,
        achievementRewardData.balance,
      );
      return (
        <StaticImage
          src={icons?.src1x}
          src2x={icons?.src2x}
          src3x={icons?.src3x}
          width={60}
        />
      );
    }
    default:
      return (
        <Avatar
          url={
            (notification.data as FollowUserNotificationData)?.followerUser
              ?.avatar
          }
          size="m"
          alt="avatar-notification"
        />
      );
  }
};

export const NotificationItem: FC<
  INotificationItemProps & HTMLAttributes<HTMLDivElement>
> = ({ notification, isInApp, onClose, ...otherProps }) => {
  const classes = useStyles();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const content = NotificationMd.getContent(notification);

  const onClickNotification = () => {
    if (!isInApp) {
      onClose?.();
    } else dispatch(closeNotification());
    if (notification?.type === UserNotificationType.FollowedByUser) {
      navigate(
        `/profile/${
          (notification.data as FollowUserNotificationData)?.followerUser?.id
        }`,
      );
    }
    return;
  };

  const onDetailNotification = () => {
    if (isInApp) {
      dispatch(closeNotification());
      return;
    }
    onClose?.();
  };

  return (
    <div className={clsx(classes.root, otherProps.className)}>
      <div className={classes.content} onClick={onClickNotification}>
        {renderAvatar(notification)}
        <div>
          <Typography variant={TypographyVariant.HeadingH6Bold}>
            {content?.title}
          </Typography>
          <Typography variant={TypographyVariant.BodyB6Regular}>
            {content?.message}
          </Typography>
        </div>
      </div>
      <Icon
        name={"icon-bold-close"}
        className={classes.icon}
        onClick={onDetailNotification}
      />
    </div>
  );
};
